import { useEffect, useState } from "react";

export const TOTAL_SELECTION = "__total__";

export type Location = Readonly<{ name: string }>;

type UseLocationSelectionsResult = Readonly<{
  selections: readonly string[];
  setSelections: (next: readonly string[]) => void;
  selectedLocations: readonly Location[];
  showTotal: boolean;
}>;

export const useLocationSelections = (
  locations: readonly Location[]
): UseLocationSelectionsResult => {
  const hasMultipleLocations = locations.length > 1;

  const [selections, setSelections] = useState<readonly string[]>([
    TOTAL_SELECTION,
  ]);

  useEffect(() => {
    if (!hasMultipleLocations) {
      return;
    }
    const validNames = new Set(locations.map((location) => location.name));
    const validSelections = selections.filter(
      (selection) => selection === TOTAL_SELECTION || validNames.has(selection)
    );
    if (validSelections.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- reset to Total when previous selections no longer exist
      setSelections([TOTAL_SELECTION]);
    } else if (validSelections.length !== selections.length) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- drop selections whose locations disappeared after a date-range change
      setSelections(validSelections);
    }
  }, [hasMultipleLocations, locations, selections]);

  const locationsByName = new Map(
    locations.map((location) => [location.name, location])
  );
  const selectedLocations = hasMultipleLocations
    ? selections
        .filter((selection) => selection !== TOTAL_SELECTION)
        .map((name) => locationsByName.get(name))
        .filter((location): location is Location => location !== undefined)
    : [];

  const showTotal = hasMultipleLocations
    ? selections.includes(TOTAL_SELECTION)
    : true;

  return { selections, setSelections, selectedLocations, showTotal };
};
